import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/InstructorSubmissions/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Checkbox,
    Divider,
    Drawer,
    IconButton,
    ScrollableTableMobileSortFilters,
    Stack,
    styled,
    Text,
} from 'shell/components';
import { SubmissionFilter } from 'utils/submissionFilters';

const FilterDrawer = ({ columns, sortState, handleSetSortState }) => {
    const { device } = useViewport();
    const { t } = useTranslation();

    const { assignment, filters, setFilters, resetFilters } = useContext(PageContext);
    const isLmsAssignment = Boolean(assignment?.api_assignment_id);
    const filterGroupCount = isLmsAssignment ? 3 : 1;

    const sortColumns = columns
        .filter(({ id }) => {
            const gradingEnabledColumns = ['identifier', 'filename', 'status', 'grade', 'time', 'grader'];
            const gradingDisabledColumns = ['identifier', 'filename', 'status'];
            return (assignment?.ai_grading_enabled ? gradingEnabledColumns : gradingDisabledColumns).includes(id);
        })
        .map((column) => {
            if (column.id === 'identifier') {
                return {
                    ...column,
                    columnTitle: t('table.last-name', 'Last Name'),
                    ascSuffix: '(A-Z)',
                    descSuffix: '(Z-A)',
                };
            }
            if (['filename', 'status', 'grader'].includes(column.id)) {
                return { ...column, ascSuffix: '(A-Z)', descSuffix: '(Z-A)' };
            }
            if (column.id === 'grade') {
                return {
                    ...column,
                    columnTitle: t('table.final-grade', 'Final Grade'),
                    ascSuffix: '(Lowest to Highest)',
                    descSuffix: '(Highest to Lowest)',
                    sortDirections: ['desc', 'asc'],
                };
            }
            return column;
        });

    const onClose = () => {
        setFilters((current) => ({ ...current, open: false }));
    };

    const toggleFilter = (category, value) => {
        setFilters((current) => ({
            ...current,
            [category]: current[category].includes(value)
                ? current[category].filter((item) => item !== value)
                : [...current[category], value],
        }));
    };

    return (
        <Drawer anchor={'left'} device={device === 'desktop' ? 'tablet' : device} open={filters.open} onClose={onClose}>
            <Stack spacing={5}>
                <IconButton
                    sx={{ alignSelf: 'flex-end' }}
                    aria-label={'close'}
                    kind={'exit'}
                    size={'medium'}
                    variant={'square'}
                    onClick={onClose}
                />

                <DrawerContent spacing={5}>
                    <Stack spacing={3}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                                {t('controls.sort-by', 'Sort By')}
                            </Text>
                            <Button variant={'borderless'} onClick={resetFilters}>
                                {t('controls.reset-all', 'Reset All')}
                            </Button>
                        </Stack>
                        <ScrollableTableMobileSortFilters
                            columns={sortColumns}
                            sortState={sortState}
                            handleSetSortState={handleSetSortState}
                        />
                    </Stack>
                    <Divider />
                    <Stack spacing={3}>
                        <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                            {t('controls.filters-count', 'Filters ({{count}})', { count: filterGroupCount })}
                        </Text>
                        <FilterCategory
                            title={t('table.status', 'Status')}
                            options={[
                                {
                                    label: t('status.ready-for-review', 'Ready for review'),
                                    value: SubmissionFilter.READY,
                                },
                                { label: t('status.reviewed', 'Reviewed'), value: SubmissionFilter.REVIEWED },
                            ]}
                            selected={filters.status}
                            onToggle={(value) => toggleFilter('status', value)}
                        />
                        {isLmsAssignment && (
                            <>
                                <FilterCategory
                                    title={t('table.lms-sync-status', 'LMS Sync Status')}
                                    options={[
                                        { label: t('tag.synced', 'Synced'), value: SubmissionFilter.SYNCED },
                                        {
                                            label: t('tag.needs-attention', 'Needs Attention'),
                                            value: SubmissionFilter.NEEDS_ATTENTION,
                                        },
                                    ]}
                                    selected={filters.lmsSyncStatus}
                                    onToggle={(value) => toggleFilter('lmsSyncStatus', value)}
                                />
                                <FilterCategory
                                    title={t('table.grade-record', 'Grade Record')}
                                    options={[
                                        {
                                            label: t('table.timelygrader-grade', 'TimelyGrader Grade'),
                                            value: SubmissionFilter.TIMELYGRADER_GRADE,
                                        },
                                        {
                                            label: t('table.lms-pushed-grade', 'LMS Pushed Grade'),
                                            value: SubmissionFilter.LMS_PUSHED_GRADE,
                                        },
                                    ]}
                                    selected={filters.gradeRecord}
                                    onToggle={(value) => toggleFilter('gradeRecord', value)}
                                />
                            </>
                        )}
                    </Stack>
                </DrawerContent>
            </Stack>
        </Drawer>
    );
};

const FilterCategory = ({ title, options, selected, onToggle }) => (
    <Stack spacing={2}>
        <Text color={'light'} variant={'p1'} weight={'semibold'}>
            {title}
        </Text>
        <CustomStack>
            {options.map(({ label, value }) => (
                <CustomCheckbox
                    key={value}
                    checked={selected.includes(value)}
                    label={label}
                    labelPlacement={'start'}
                    onChange={() => onToggle(value)}
                />
            ))}
        </CustomStack>
    </Stack>
);

const DrawerContent = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(0, 5),
}));

const CustomStack = styled(Stack)(({ theme }) => ({
    paddingLeft: theme.spacing(4),
}));

const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
    justifyContent: 'space-between',
    '& > .MuiTypography-root': {
        color: theme.palette.ensightful.neutral[700],
    },
}));

export default FilterDrawer;
